import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { toSanitizedMarkdownHtml } from "../markdown.ts";

@customElement("file-preview-text")
export class FilePreviewText extends LitElement {
  @property({ type: String }) url = "";
  @property({ type: String }) fileType = "text";
  @property({ type: String }) fileName = "";

  @state() private content = "";
  @state() private loading = true;
  @state() private error: string | null = null;

  static styles = css`
    :host {
      display: block;
      height: 100%;
      overflow: auto;
      padding: 12px;
    }
    .preview-loading {
      color: var(--muted, #888);
      padding: 16px;
    }
    .preview-error {
      color: var(--danger, #e53935);
      padding: 16px;
    }
    .preview-markdown {
      font-size: 0.9em;
      line-height: 1.5;
    }
    .preview-text {
      white-space: pre-wrap;
      font-family: var(--mono-font, monospace);
      font-size: 0.85em;
      line-height: 1.4;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.loadContent();
  }

  private async loadContent() {
    if (!this.url) return;
    this.loading = true;
    this.error = null;
    try {
      const res = await fetch(this.url);
      if (!res.ok) {
        this.error = `Failed to load: ${res.status} ${res.statusText}`;
        return;
      }
      this.content = await res.text();
    } catch (err) {
      this.error = String(err);
    } finally {
      this.loading = false;
    }
  }

  render() {
    if (this.loading) {
      return html`<div class="preview-loading">Loading ${this.fileName}…</div>`;
    }
    if (this.error) {
      return html`<div class="preview-error">${this.error}</div>`;
    }
    if (this.fileType === "markdown") {
      return html`<div class="preview-markdown">
        ${unsafeHTML(toSanitizedMarkdownHtml(this.content))}
      </div>`;
    }
    return html`<pre class="preview-text">${this.content}</pre>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "file-preview-text": FilePreviewText;
  }
}
